import { context } from '@shared-services/context-service';
import { useEditorContext } from 'client/infra/renderers/editorState';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useFilterSortLogic from './components/useFilterSortLogic';
import { hasProductGalleryOnPage } from './components/utils';
import { FilterSortExtensionData } from './components/utils/types';

export default function useFilterSortWidget(props: FilterSortExtensionData) {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [showWarning, setShowWarning] = useState(false);
    const [openedMenuItemId, setOpenedMenuItemId] = useState<string | null>(
        'item-sort'
    );
    const { isInEditor } = useEditorContext();

    const {
        filterableFields = [],
        sortableFields,
        collectionValue,
        isSortSectionHidden,
        hasNativeStore,
        isDesignOpen,
        isContentOpen,
    } = props;

    const filterSortLogic = useFilterSortLogic(collectionValue, {
        isSortSectionHidden,
        sortableFields,
    });

    const allOptionsAreDisabled = useMemo(() => {
        const isContentHidden =
            props.isFilterSectionHidden && props.isSortSectionHidden;

        if (isContentHidden) {
            return true;
        }
        const enabledItem = [
            ...(props.isFilterSectionHidden ? [] : filterableFields),
            ...(props.isSortSectionHidden ? [] : sortableFields),
        ].some((item) => !item.disabled);

        return !enabledItem;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterableFields, sortableFields]);

    useEffect(() => {
        const shouldShowWarning = !hasNativeStore || !hasProductGalleryOnPage();

        setShowWarning(!!isInEditor && shouldShowWarning);

        if (isInEditor && isMenuOpen) {
            setIsMenuOpen(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isInEditor]);

    const onBtnClick = () => {
        if (!context.isEditor) {
            setIsMenuOpen((open) => !open);
        }
    };

    const shouldRenderMenu =
        (isMenuOpen || isDesignOpen || isContentOpen) &&
        filterSortLogic &&
        !allOptionsAreDisabled;

    const toggleMenuItem = useCallback((id: string) => {
        setOpenedMenuItemId((prev) => (prev === id ? null : id));
    }, []);

    return {
        filterSortLogic,
        onBtnClick,
        showWarning,
        setIsMenuOpen,
        shouldRenderMenu,
        openedMenuItemId,
        toggleMenuItem,
    };
}
